import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import { GET_USER, GET_ALL_USER_MANAGEMENT_LIST, GET_ALL_USER_GROUPS, GET_ALL_INSTITUTIONS, GET_ALL_LHD, UPDATE_USER_DATA } from "../../../queries/ddwAdminQrys/UserManagementQrys";
import { PageSpinner } from "../../reuseableComponents/spinners/PageSpinner";
import {
  Table,
  TableBody,
  TableBodyData,
  TableBodyDataCellTemplate,
  TableBodyDataRowTemplate,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TablePagination,
  TableWrapper,
  TableFilterTextInput,
  TableFilterNone,
  TableFilters,
  TableSortingRules,
  TableSortingRule,
  Modal,
  ModalTitle,
  ModalContent,
  ModalFooter,
  Button,
  TextInput,
  MultiSelect,
  MultiSelectOption,
  Select,
  SelectOption
} from '@utahdts/utah-design-system';

import "@utahdts/utah-design-system-header/css";
import "./userManagementTable.css";
import { formatDate } from "../../../utilities/commonFunctions/formattingFunctions";
import MultiSelectWaterSystems from "../../reuseableComponents/waterSystems/MultiSelectWaterSystems";

const UserManagementTable = () => {
  const { umd_id } = useParams();
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Fetching Group types:
  const [groupTypes, setGroupTypes] = useState([]);
  const [getUserGroupTypes] = useLazyQuery(GET_ALL_USER_GROUPS);

  // Fetching Institutions Types
  const [institutionTypes, setInstitutionTypes] = useState([]);
  const [getInstitutionTypes] = useLazyQuery(GET_ALL_INSTITUTIONS);

  // Fetching LHD Types
  const [lhdTypes, setLHDTypes] = useState([]);
  const [getLHDTypes] = useLazyQuery(GET_ALL_LHD);

  const [getUser] = useLazyQuery(GET_USER, {
    fetchPolicy: "no-cache"
  });
  const latestEditRequestRef = useRef(0);

  // State to store selected values
  const [selectedGroups, setSelectedGroups] = useState();
  const [selectedWaterSystems, setSelectedWaterSystems] = useState();
  const [assignedWaterSystems, setAssignedWaterSystems] = useState([]);
  const [selectedInstitutions, setSelectedInstitutions] = useState();
  const [selectedLhds, setSelectedLhds] = useState();

  const [updateUserMutation] = useMutation(UPDATE_USER_DATA);

  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [userUmdId, setUserUmdId] = useState(umd_id);

  // Fetching logged in user data from localStorage.
  const [user] = useState(
    localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : { isPublic: true }
  );

  const permissions = user?.permissions || [];

  const canEditPortalGroups= permissions.includes("portal_user_management");
  const canEditOtherGroups= permissions.includes("staff_user_management");

  const hydrateEditModalState = useCallback((fullUser) => {
    if (!fullUser) {
      return;
    }

    setSelectedUser(fullUser);
    setSelectedGroups((fullUser.assignedGroups || []).map((group) => group.group_desc));
    setSelectedWaterSystems((fullUser.assignedSystems || []).map((ws) => ws.systemName));
    setAssignedWaterSystems((fullUser.assignedSystems || []).map((ws) => ({
      systemId: String(ws.systemId),
      systemName: ws.systemName
    })));
    setSelectedInstitutions((fullUser.assignedInstitutions || []).map((inst) => inst.institutionName));
    setSelectedLhds((fullUser.assignedLhds || []).map((lhd) => lhd.lhd_name.toUpperCase().trim()));
    setShowModal(true);
  }, []);

  useEffect(() => {
    Promise.all([
      getUserGroupTypes(),
      getInstitutionTypes(),
      getLHDTypes()
    ])
      .then(([groupTypesResult, InstitutionTypesResult, lhdTypeResult]) => {
        if (groupTypesResult.data?.getAllUserGroups) {
          setGroupTypes(groupTypesResult.data.getAllUserGroups);
        }
        if (InstitutionTypesResult.data?.getAllInstitutions) {
          setInstitutionTypes(InstitutionTypesResult.data.getAllInstitutions);
        }
        if (lhdTypeResult.data?.getAllLHD) {
          setLHDTypes(lhdTypeResult.data.getAllLHD);
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }, [getUserGroupTypes, getInstitutionTypes, getLHDTypes]);

  useEffect(() => {
    if (userUmdId) {
      const requestId = latestEditRequestRef.current + 1;
      latestEditRequestRef.current = requestId;

      getUser({ variables: { umdId: userUmdId } }).then((data) => {
        if (requestId !== latestEditRequestRef.current) {
          return;
        }

        if (data?.data?.getUser) {
          hydrateEditModalState(data.data.getUser);
          setUserUmdId(null);
        }
      }).catch((error) => {
        console.error("Error fetching user data:", error);
      });
    }
  }, [userUmdId, getUser, hydrateEditModalState]);

  const getDateTimestamp = useCallback((dateInput) => {
    if (!dateInput) return null;

    const parsedDate =
      typeof dateInput === "number" || (typeof dateInput === "string" && /^\d+$/.test(dateInput))
        ? new Date(Number.parseInt(dateInput, 10))
        : new Date(dateInput);

    const timestamp = parsedDate.getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }, []);

  const processUserData = useCallback((userData) => {
    return userData.map((user) => ({
      ...user,
      fullname: user.firstName + " " + user.lastName,
      loginTimestamp: getDateTimestamp(user.lastlogin),
      loginDate: user.lastlogin ? formatDate(user.lastlogin, { month: '2-digit', day: '2-digit', hour: undefined, minute: undefined, second: undefined, hour12: undefined }) : null,
      status: user.status,
    }));
  }, [getDateTimestamp]);

  const { loading: usersLoading, error: usersError, data: userDataList, refetch } = useQuery(GET_ALL_USER_MANAGEMENT_LIST);

  useEffect(function _handleGetAllUsers() {
    if (userDataList?.getAllUserManagementList) {
      const processedUserData = processUserData(userDataList.getAllUserManagementList);
      setUsers(processedUserData);
    } else if (!usersLoading) {
      if (usersError) {
        console.error("Error fetching user list:", usersError);
      } else {
        console.error("getAllUserManagementList data is missing or invalid:", userDataList);
      }
      setUsers([]);
    }
  }, [userDataList, usersLoading, usersError, setUsers, processUserData]);

  const editUserModalButton = ({ record }) => {
    return (<button className="link-button" onClick={() => handleEditClick(record)}>Edit</button>)
  }

  const handleEditClick = (user) => {
    const requestId = latestEditRequestRef.current + 1;
    latestEditRequestRef.current = requestId;

    setShowModal(false);
    setSelectedUser(null);
    setSelectedGroups([]);
    setSelectedWaterSystems([]);
    setAssignedWaterSystems([]);
    setSelectedInstitutions([]);
    setSelectedLhds([]);

    // Fetch user-specific data
    getUser({ variables: { umdId: user.umd_id } }).then((data) => {
      if (requestId !== latestEditRequestRef.current) {
        return;
      }

      if (data?.data?.getUser) {
        hydrateEditModalState(data.data.getUser);
      }
    })
      .catch((error) => {
        console.error("Error fetching user data:", error);
      });
  };

  const handleGroupChange = (selectedValues) => {
    setSelectedGroups(selectedValues);
  };

  const handleWaterSystemChange = (selectedValues) => {
    setSelectedWaterSystems(selectedValues);
  };

  const handleAssignedWaterSystemsChange = (assignedSystems) => {
    const nextAssignedSystems = Array.isArray(assignedSystems) ? assignedSystems : [];

    setAssignedWaterSystems((previousAssignedSystems) => {
      if (previousAssignedSystems.length === nextAssignedSystems.length
        && previousAssignedSystems.every((system, index) => (
          system?.systemId === nextAssignedSystems[index]?.systemId
          && system?.systemName === nextAssignedSystems[index]?.systemName
        ))) {
        return previousAssignedSystems;
      }

      return nextAssignedSystems;
    });
  };

  const handleInstChange = (selectedValues) => {
    setSelectedInstitutions(selectedValues);
  };

  const handleLHDChange = (selectedValues) => {
    setSelectedLhds(selectedValues);
  };

  const handleSubmit = () => {
    if (!selectedUser) return;

    // Data to be sent
    const updatedUser = {
      umd_id: selectedUser.umd_id,
      firstName: selectedUser.firstName,
      lastName: selectedUser.lastName,
      email: selectedUser.email,
      status: selectedUser.status,
      updatedBy: "adminuser@utah.gov",
      assignedGroups: (selectedGroups || []).map(groupName => {
        const group = groupTypes.find(gt => gt.group_desc === groupName);
        return group
          ? { user_group: group.user_group, group_desc: group.group_desc, is_portal_group: group.is_portal_group, umd_id: selectedUser.umd_id }
          : null;
      }).filter(Boolean),
      assignedSystems: (assignedWaterSystems || []).filter((system) => (
        system && system.systemId !== undefined && system.systemId !== null && system.systemName
      )),
      assignedInstitutions: (selectedInstitutions || []).map(instName => {
        const inst = institutionTypes.find(it => it.institutionName === instName);
        return inst ? { institutionId: inst.institutionId, institutionName: inst.institutionName } : null;
      }).filter(Boolean),
      assignedLhds: (selectedLhds || []).map(lhdName => {
        const lhd = lhdTypes.find(lt => lt.lhd_name.toUpperCase().trim() === lhdName.toUpperCase().trim());
        return lhd ? { lhd_id: lhd["lhd_id"], lhd_name: lhd.lhd_name } : null;
      }).filter(Boolean)
    };

    updateUserMutation({
      variables: {
        input: updatedUser,
      },
    })
      .then(() => {
        setUpdateSuccess(true);
        refetch();
        setTimeout(() => {
          setShowModal(false);
          setUpdateSuccess(false);
        }, 2000);
      })
      .catch((error) => {
        console.error("Error updating user:", error);
      });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setSelectedGroups([]);
    setSelectedWaterSystems([]);
    setAssignedWaterSystems([]);
    setSelectedInstitutions([]);
    setSelectedLhds([]);
  };

  const handleOnChange = (event) => {
    const { name, value } = event.target;
    setSelectedUser(prevUser => ({
      ...prevUser,
      [name]: value,
      ...(name === 'active' && { accountStatus: value }),
    }));
  };

  return (
    <div className="page-container user-management-table-container">
      <div className="utah-design-system">
        {usersLoading && (
          <PageSpinner />
        )}
        <TableWrapper id="user-management-table">
          <Table className="table--alt">
            <TableHead>
              <TableSortingRules>
                <TableSortingRule allyLabel="fullname" recordFieldPath="fullname" />
                <TableSortingRule allyLabel="email" recordFieldPath="email" />
                <TableSortingRule allyLabel="groups" recordFieldPath="groups" />
                <TableSortingRule allyLabel="loginDate" recordFieldPath="loginTimestamp" fieldType="number" />
                <TableSortingRule allyLabel="status" recordFieldPath="status" />
              </TableSortingRules>
              <TableHeadRow>
                <TableHeadCell recordFieldPath="fullname">Name</TableHeadCell>
                <TableHeadCell recordFieldPath="email">Email</TableHeadCell>
                <TableHeadCell recordFieldPath="groups">Groups</TableHeadCell>
                <TableHeadCell recordFieldPath="loginDate" tableSortingFieldPaths={["loginTimestamp"]}>Last Login</TableHeadCell>
                <TableHeadCell recordFieldPath="status">Status</TableHeadCell>
                <TableHeadCell recordFieldPath="edit" aria-label="Actions">
                  <span className="visually-hidden">Actions</span>
                </TableHeadCell>
              </TableHeadRow>
              <TableFilters>
                <TableFilterTextInput recordFieldPath="fullname" placeholder={'filter'} />
                <TableFilterTextInput recordFieldPath="email" placeholder={'filter'} />
                <TableFilterTextInput recordFieldPath="groups" placeholder={'filter'} />
                <TableFilterTextInput recordFieldPath="loginDate" placeholder={'filter'} />
                <TableFilterTextInput recordFieldPath="status" placeholder={'filter'} />
                <TableFilterNone aria-label="Actions">
                  <span className="visually-hidden">Actions</span>
                </TableFilterNone>
              </TableFilters>
            </TableHead>
            <TableBody>
              <TableBodyData records={users} recordIdField="umd_id">
                <TableBodyDataRowTemplate>
                  <TableBodyDataCellTemplate recordFieldPath="fullname" />
                  <TableBodyDataCellTemplate recordFieldPath="email" />
                  <TableBodyDataCellTemplate recordFieldPath="groups" />
                  <TableBodyDataCellTemplate recordFieldPath="loginDate" />
                  <TableBodyDataCellTemplate recordFieldPath="status" />
                  <TableBodyDataCellTemplate className="action">{editUserModalButton}</TableBodyDataCellTemplate>
                </TableBodyDataRowTemplate>
              </TableBodyData>
            </TableBody>
          </Table>
          <TablePagination
            className="mt-spacing"
            id="user-managemenet-table-pagination'"
            itemsPerPage={15}
          />
        </TableWrapper>

        {showModal && (
          <Modal className="modal--large">
            <ModalTitle>Edit User</ModalTitle>
            <ModalContent className="modal__content">
              {/* Display user details for editing */}
              {selectedUser && (
                <div className="form-grid">
                  <p className="grid-full-width">Email: {selectedUser.email}</p>
                  <TextInput name="firstName"
                    id="edit-firstName"
                    label="First Name"
                    value={selectedUser.firstName ? selectedUser.firstName : ""}
                    onChange={handleOnChange} />

                  <TextInput name="lastName"
                    id="edit-lastName"
                    label="Last Name"
                    value={selectedUser.lastName ? selectedUser.lastName : ""}
                    onChange={handleOnChange} />

                    <MultiSelect
                      id="portalgroups"
                      isDisabled ={!canEditPortalGroups}
                      label="Portal Groups"
                      onChange={handleGroupChange}
                      values={selectedGroups}
                    >
                      {groupTypes
                          .filter(gt => gt.is_portal_group === '1')
                          .map(gt => (
                        <MultiSelectOption
                          key={gt.user_group}
                          label={gt.group_desc}
                          value={gt.group_desc}
                        />
                      ))}
                    </MultiSelect>

                    <MultiSelect
                      id="othergroups"
                      isDisabled = {!canEditOtherGroups}
                      label="Other Groups"
                      onChange={handleGroupChange}
                      values={selectedGroups}
                    >
                      {groupTypes
                      .filter(gt => gt.is_portal_group !== '1')
                      .map(gt => (
                        <MultiSelectOption
                          key={gt.user_group}
                          label={gt.group_desc}
                          value={gt.group_desc}
                        />
                      ))}
                    </MultiSelect>

                    <MultiSelectWaterSystems
                      id="water_systems"
                      label="Water Systems"
                      onChange={handleWaterSystemChange}
                      selectedWaterSystems={selectedWaterSystems}
                      existingAssignedSystems={assignedWaterSystems}
                      onAssignedSystemsChange={handleAssignedWaterSystemsChange}
                    />

                    <MultiSelect
                      id="institutions"
                      label="Institutions"
                      onChange={handleInstChange}
                      values={selectedInstitutions}
                    >
                      {institutionTypes.map((inst) => (
                        <MultiSelectOption
                          key={inst.institutionId}
                          label={inst.institutionName}
                          value={inst.institutionName}
                        />
                      ))}
                    </MultiSelect>

                    <MultiSelect
                      id="lhd"
                      label="LHD"
                      onChange={handleLHDChange}
                      values={selectedLhds}
                    >
                      {lhdTypes.map((lhd) => (
                        <MultiSelectOption
                          key={lhd.lhd_id}
                          label={lhd.lhd_name}
                          value={lhd.lhd_name}
                        />
                      ))}
                    </MultiSelect>

                  <div className='input-wrapper'>
                    <Select
                      id="status"
                      name="status"
                      label="Status"
                      value={selectedUser.status}
                      onChange={handleOnChange}
                    >

                      <SelectOption value="Active" label="Active" />
                      <SelectOption value="Pending" label="Pending" />
                      <SelectOption value="Inactive" label="Inactive" />
                    </Select>
                  </div>

                  <dl className="input-wrapper">
                    <dt>Account Name</dt>
                    <dd>{selectedUser.accountName}</dd>
                  </dl>
                  <dl className="input-wrapper">
                    <dt>Section</dt>
                    <dd>{selectedUser.section}</dd>
                  </dl>
                  <dl className="input-wrapper">
                    <dt>Manager Name</dt>
                    <dd>{selectedUser.manager}</dd>
                  </dl>
                  <dl className="input-wrapper">
                    <dt>Account Description/Justification</dt>
                    <dd>{selectedUser.accountDescription}</dd>
                  </dl>
                  <dl className="input-wrapper">
                    <dt>Request Access Type</dt>
                    <dd>{selectedUser.requestAccessType}</dd>
                  </dl>
                  <dl className="input-wrapper">
                    <dt>Created</dt>
                    <dd>{selectedUser.createdAt ? formatDate(selectedUser.createdAt) : 'N/A'}</dd>
                    <dd>{selectedUser.createdBy}</dd>
                  </dl>
                  <dl className="input-wrapper">
                    <dt>Last Updated</dt>
                    <dd>{selectedUser.updatedAt ? formatDate(selectedUser.updatedAt) : 'N/A'}</dd>
                    <dd>{selectedUser.updatedBy}</dd>
                  </dl>
                  <dl className="input-wrapper">
                    <dt>Last Login</dt>
                    <dd>{selectedUser.lastlogin ? formatDate(selectedUser.lastlogin, { hour: undefined, minute: undefined, second: undefined, hour12: undefined }) : 'N/A'}</dd>
                  </dl>
                </div>
              )}
            </ModalContent>
            <ModalFooter>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
                {updateSuccess && (
                  <div style={{ color: 'green', marginRight: 'auto' }}>
                    User updated successfully.
                  </div>
                )}
                <div className="button-container" style={{ display: 'flex' }}>
                  <Button variant="primary" onClick={handleSubmit}>Save Changes</Button>
                  <Button variant="secondary" onClick={handleCloseModal}>
                    Close
                  </Button>
                </div>
              </div>
            </ModalFooter>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default UserManagementTable;
